import { useState } from 'react';
import { ClockIcon, HourglassIcon, RotateCcwIcon, SendIcon, TriangleAlertIcon } from 'lucide-react';
import {
  NotificationStatus,
  NotificationType,
  type AdminNotificationFilter,
  type AdminNotificationRow,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { PagerControls } from '@/components/pager';
import { StatCard } from '@/components/stat-card';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import {
  useAdminNotifications,
  useNotificationStats,
  useRetryNotifications,
} from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { strings } from '@/i18n/strings';

const COLUMNS = 8;

const TYPE_LABEL: Record<string, string> = {
  [NotificationType.RECEIPT_REVOKED]: strings.notifications.typeReceiptRevoked,
  [NotificationType.RECEIPT_POINTS_CREDITED]: strings.notifications.typeReceiptPoints,
  [NotificationType.DRAW_WIN]: strings.notifications.typeDrawWin,
  [NotificationType.DRAW_REMINDER]: strings.notifications.typeDrawReminder,
  [NotificationType.DRAW_RESULT]: strings.notifications.typeDrawResult,
  [NotificationType.REFERRAL_BONUS]: strings.notifications.typeReferralBonus,
  [NotificationType.REWARD_DELIVERED]: strings.notifications.typeRewardDelivered,
  [NotificationType.REWARD_CANCELLED]: strings.notifications.typeRewardCancelled,
};

const STATUS_LABEL: Record<string, string> = {
  [NotificationStatus.QUEUED]: strings.notifications.statusQueued,
  [NotificationStatus.SENT]: strings.notifications.statusSent,
  [NotificationStatus.FAILED]: strings.notifications.statusFailed,
};

const BLOCKED_LABEL: Record<string, string> = {
  user_blocked: strings.notifications.blockedUserBlocked,
  bot_blocked: strings.notifications.blockedBotBlocked,
  chat_not_found: strings.notifications.blockedChatNotFound,
  already_sent: strings.notifications.blockedAlreadySent,
  not_stuck: strings.notifications.blockedNotStuck,
};

function moment(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('uz-UZ', { timeZone: 'Asia/Tashkent' }) : '—';
}

/**
 * Nega xabar bormadi — bitta katakda.
 *
 * Ikkita boshqa-boshqa narsa bor va ularni aralashtirmaslik kerak: Telegram QAYTARGAN matn
 * (`lastError`) va bizning tasnifimiz (`blockedReason`). Birinchisi nima bo'lganini aytadi,
 * ikkinchisi nima uchun tugma bosilmasligini. Faqat birinchisini ko'rsatish operatorni
 * ishlamaydigan tugmani bosishga qoldirardi; faqat ikkinchisini — sababni yashirardi.
 */
function Reason({ row }: { row: AdminNotificationRow }) {
  const blocked = row.blockedReason ? BLOCKED_LABEL[row.blockedReason] : null;
  if (!row.lastError && !blocked) {
    return <span className="text-muted-foreground text-xs">{strings.notifications.noReason}</span>;
  }
  return (
    <div className="min-w-0 space-y-0.5">
      {row.lastError && (
        <div className="font-mono text-xs break-words" title={row.lastError}>
          {row.lastError}
        </div>
      )}
      {blocked && <div className="text-muted-foreground text-xs">{blocked}</div>}
    </div>
  );
}

/**
 * Xabarlar navbatining monitoringi (§3.8).
 *
 * Sahifaning maqsadi bitta savol: nega falon odamga xabar bormadi. Shuning uchun ustunlar
 * ham shunga qarab tanlangan — status va urinishlar soni emas, SABAB va uni tuzatish
 * imkoniyati markazda.
 */
export function NotificationsPage() {
  // Sahifa QOTGANLARI bilan ochiladi: operator bu yerga "hammasi qanday" deb emas, "nega
  // bormadi" deb keladi, va standart bo'sh filtr javobni yuborilganlar ostiga ko'mib qo'yardi.
  const [filter, setFilter] = useState<AdminNotificationFilter>({ stuck: true });
  const pager = useOffsetPager();

  const stats = useNotificationStats();
  const list = useAdminNotifications({ ...filter, page: pager.page, limit: pager.limit });
  const retry = useRetryNotifications();
  const [notice, setNotice] = useState<string | null>(null);

  /** Har bir filtr o'zgarishi birinchi sahifaga qaytaradi — aks holda bo'sh sahifa ochilardi. */
  function update(patch: Partial<AdminNotificationFilter>) {
    setFilter((current) => ({ ...current, ...patch }));
    pager.reset();
    setNotice(null);
  }

  async function runRetry(body: Parameters<typeof retry.mutateAsync>[0]) {
    const result = await retry.mutateAsync(body);
    setNotice(
      result.skippedCount > 0
        ? `${strings.notifications.retryDone}: ${result.retriedCount} — ${result.skippedCount} ${strings.notifications.retrySkipped}`
        : `${strings.notifications.retryDone}: ${result.retriedCount}`,
    );
  }

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={strings.notifications.title}
        description={strings.notifications.subtitle}
      />

      {/*
        Har bir sanoq o'z belgisi bilan: bu qator paneldagi boshqa besh-oltita shunday qator
        bilan yonma-yon ko'riladi va belgisiz variant ular orasida "tugallanmagan" bo'lib
        turardi. Ohang esa RAQAMDAN olinadi — qotib qolgan xabar bo'lsa kartochka sariq
        chegara oladi, bo'lmasa fonga chekinadi.
      */}
      <div className="motion-stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard
          label={strings.notifications.statQueued}
          value={stats.data === undefined ? '—' : formatNumber(stats.data.queued)}
          icon={ClockIcon}
        />
        <StatCard
          label={strings.notifications.statSent}
          value={stats.data === undefined ? '—' : formatNumber(stats.data.sent)}
          icon={SendIcon}
        />
        <StatCard
          label={strings.notifications.statFailed}
          value={stats.data === undefined ? '—' : formatNumber(stats.data.failed)}
          icon={TriangleAlertIcon}
          tone={(stats.data?.failed ?? 0) > 0 ? 'warning' : 'muted'}
        />
        <StatCard
          label={strings.notifications.statStuck}
          value={stats.data === undefined ? '—' : formatNumber(stats.data.stuck)}
          icon={HourglassIcon}
          tone={(stats.data?.stuck ?? 0) > 0 ? 'warning' : 'muted'}
          hint={
            stats.data?.oldestStuckMinutes != null
              ? `${strings.notifications.oldestStuck}: ${stats.data.oldestStuckMinutes} ${strings.notifications.minutesAgo}`
              : undefined
          }
        />
        <StatCard
          label={strings.notifications.statRetryable}
          value={stats.data === undefined ? '—' : formatNumber(stats.data.retryable)}
          icon={RotateCcwIcon}
        />
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-6">
          <Field label={strings.notifications.filterStatus}>
            <Select
              value={filter.status ?? ''}
              onChange={(event) =>
                update({
                  status: (event.target.value || undefined) as AdminNotificationFilter['status'],
                })
              }
            >
              <option value="">{strings.notifications.all}</option>
              {Object.entries(STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={strings.notifications.filterType}>
            <Select
              value={filter.type ?? ''}
              onChange={(event) =>
                update({
                  type: (event.target.value || undefined) as AdminNotificationFilter['type'],
                })
              }
            >
              <option value="">{strings.notifications.all}</option>
              {Object.entries(TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={strings.notifications.filterQuery}
            hint={strings.notifications.filterQueryHint}
          >
            <Input
              value={filter.query ?? ''}
              onChange={(event) => update({ query: event.target.value || undefined })}
            />
          </Field>

          <Field label={strings.notifications.filterFrom}>
            <Input
              type="date"
              value={filter.from ?? ''}
              onChange={(event) => update({ from: event.target.value || undefined })}
            />
          </Field>

          <Field label={strings.notifications.filterTo}>
            <Input
              type="date"
              value={filter.to ?? ''}
              onChange={(event) => update({ to: event.target.value || undefined })}
            />
          </Field>

          <div className="flex flex-col justify-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filter.stuck ?? false}
                onChange={(event) => update({ stuck: event.target.checked || undefined })}
              />
              {strings.notifications.filterStuck}
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilter({});
                pager.reset();
              }}
            >
              {strings.notifications.filterReset}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={retry.isPending || (stats.data?.retryable ?? 0) === 0}
          onClick={async () => {
            if (!window.confirm(strings.notifications.retryAllConfirm)) return;
            await runRetry({ all: true, filter });
          }}
        >
          <RotateCcwIcon className="size-4" aria-hidden />
          {retry.isPending ? strings.notifications.retrying : strings.notifications.retryAll}
        </Button>
        {notice && (
          <span role="status" className="text-muted-foreground text-sm">
            {notice}
          </span>
        )}
      </div>
      {retry.isError && <ErrorNotice error={retry.error} />}

      <Card>
        <CardHeader>
          <CardTitle>{strings.notifications.title}</CardTitle>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <TableSkeleton rows={8} columns={COLUMNS} />
          ) : list.isError ? (
            <ErrorNotice error={list.error} />
          ) : rows.length === 0 ? (
            <EmptyState title={strings.notifications.empty} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{strings.notifications.columnUser}</TableHead>
                    <TableHead>{strings.notifications.columnType}</TableHead>
                    <TableHead>{strings.notifications.columnStatus}</TableHead>
                    <TableHead>{strings.notifications.columnAttempts}</TableHead>
                    <TableHead>{strings.notifications.columnCreated}</TableHead>
                    <TableHead>{strings.notifications.columnScheduled}</TableHead>
                    <TableHead>{strings.notifications.columnReason}</TableHead>
                    <TableHead>{strings.notifications.columnAction}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="text-sm">{row.displayName || '—'}</div>
                        <div className="text-muted-foreground font-mono text-xs">
                          {row.telegramId}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{TYPE_LABEL[row.type] ?? row.type}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant={row.status === 'failed' ? 'destructive' : 'secondary'}>
                            {STATUS_LABEL[row.status] ?? row.status}
                          </Badge>
                          {row.stuck && (
                            <Badge variant="outline">{strings.notifications.stuckBadge}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{row.attempts}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {moment(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {moment(row.sentAt ?? row.scheduledFor)}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <Reason row={row} />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!row.retryable || retry.isPending}
                          onClick={() => void runRetry({ ids: [row.id] })}
                        >
                          {strings.notifications.retry}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <PagerControls
            pager={pager}
            total={list.data?.total}
            pageCount={list.data?.pageCount}
            rowCount={rows.length}
            busy={list.isFetching}
            label={strings.notifications.title}
          />
        </CardContent>
      </Card>
    </div>
  );
}
